import { Injectable } from "@angular/core";
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { SentryService } from "../services/sentry.service";

/**
 * PROSP-654 — conflitos de DOMÍNIO previstos, que o backend devolve com `identificador`
 * estável e o usuário resolve sozinho na própria tela.
 *
 * Não são incidente: a regra de negócio funcionou. Reportá-los aqui apenas moveria para
 * o projeto do frontend o ruído que o backend deixou de gerar, e o alerta continuaria
 * acionando o time sem haver indisponibilidade.
 *
 * A lista é de IDENTIFICADORES, e não de status: nem todo 409 é esperado, e silenciar a
 * faixa inteira esconderia conflitos que ninguém tratou. Entra aqui só o que tem
 * tratamento de tela comprovado por teste — hoje, a agenda duplicada. O breadcrumb
 * continua sendo gravado, então o evento segue visível no contexto de qualquer erro
 * posterior.
 */
const CONFLITOS_ESPERADOS = ["CONFLITO_AGENDA_DUPLICADA"];

@Injectable()
export class SentryHttpInterceptor implements HttpInterceptor {
    constructor(private sentryService: SentryService) {}

    intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        // Capturar informações da requisição
        const startTime = Date.now();
        const endpoint = this.extractEndpoint(request.url);
        const method = request.method;

        // Adicionar breadcrumb da requisição
        this.sentryService.addBreadcrumb(
            `API Request: ${method} ${endpoint}`,
            "http",
            {
                url: request.url,
                method: method,
                body: this.sanitizeRequestBody(request.body),
            },
            "info"
        );

        return next.handle(request).pipe(
            tap((event: HttpEvent<unknown>) => {
                // Capturar resposta bem-sucedida para métricas
                if (event instanceof HttpResponse) {
                    const duration = Date.now() - startTime;

                    // Log apenas se demorou mais que 2 segundos (performance issue)
                    if (duration > 2000) {
                        this.sentryService.addBreadcrumb(
                            `Slow API Response: ${method} ${endpoint} (${duration}ms)`,
                            "http.performance",
                            {
                                url: request.url,
                                method: method,
                                duration: duration,
                                status: event.status,
                            },
                            "warning"
                        );
                    }
                }
            }),
            catchError((error: HttpErrorResponse) => {
                // PROSP-654: conflito de domínio previsto não vira issue. O erro SEGUE
                // normalmente para o componente, que é quem avisa o usuário.
                if (!SentryHttpInterceptor.ehConflitoEsperado(error)) {
                    this.captureApiError(error, request, startTime);
                }

                // Continuar com o erro para que os componentes possam tratá-lo
                return throwError(() => error);
            })
        );
    }

    /**
     * PROSP-654 — o erro é um conflito de domínio previsto e já tratado na tela?
     *
     * Exige status 409 E identificador conhecido. Só o status silenciaria conflitos
     * novos, ainda sem tratamento; só o identificador confiaria num corpo que pode vir
     * vazio (timeout, proxy, resposta não-JSON).
     *
     * @param error erro HTTP recebido
     * @returns true quando o evento não deve gerar issue no Sentry
     */
    static ehConflitoEsperado(error: HttpErrorResponse): boolean {
        const identificador = (error?.error as { identificador?: string } | null)?.identificador;

        return error?.status === 409
            && typeof identificador === "string"
            && CONFLITOS_ESPERADOS.includes(identificador);
    }

    /**
     * Captura erro de API com contexto completo
     */
    private captureApiError(
        error: HttpErrorResponse,
        request: HttpRequest<unknown>,
        startTime: number
    ): void {
        const endpoint = this.extractEndpoint(request.url);
        const method = request.method;
        const duration = Date.now() - startTime;

        // Contexto detalhado do erro
        const context = {
            api_endpoint: endpoint,
            full_url: request.url,
            http_method: method,
            request_headers: this.sanitizeHeaders(request.headers),
            request_body: this.sanitizeRequestBody(request.body),
            response_status: error.status,
            response_status_text: error.statusText,
            response_data: this.sanitizeResponseBody(error.error),
            response_headers: this.sanitizeHeaders(error.headers),
            duration_ms: duration,
            timestamp: new Date().toISOString(),
        };

        // Adicionar contexto específico baseado no endpoint
        this.addEndpointSpecificContext(endpoint, method, context);

        // Tags para filtros rápidos no Sentry
        this.sentryService.setTag("api_endpoint", endpoint);
        this.sentryService.setTag("http_method", method);
        this.sentryService.setTag("http_status", error.status.toString());
        this.sentryService.setTag("api_error", "true");

        // Criar mensagem de erro mais descritiva
        const errorMessage = this.createErrorMessage(error, method, endpoint);

        // Capturar no Sentry
        this.sentryService.captureError(new Error(errorMessage), context);

        // Adicionar breadcrumb do erro
        this.sentryService.addBreadcrumb(
            `API Error: ${method} ${endpoint} - ${error.status}`,
            "http.error",
            {
                status: error.status,
                message: error.message,
                duration: duration,
            },
            "error"
        );
    }

    /**
     * Extrai o endpoint da URL completa
     */
    private extractEndpoint(url: string): string {
        try {
            // Remove a base URL para manter apenas o endpoint
            const urlObj = new URL(url);
            return urlObj.pathname + (urlObj.search || "");
        } catch {
            // Se não conseguir fazer parse da URL, retorna a URL original
            return url;
        }
    }

    // TODO: ADD ALL THE ENDPOINTS HERE
    /**
     * Adiciona contexto específico baseado no endpoint
     */
    private addEndpointSpecificContext(
        endpoint: string,
        method: string,
        context: any
    ): void {
        // Identificar módulo baseado no endpoint
        if (endpoint.includes("/boletas")) {
            context.business_module = "boleta-intermediacao";
            this.sentryService.setTag(
                "business_module",
                "boleta-intermediacao"
            );

            // Identificar operação específica
            if (endpoint.includes("/revalidar")) {
                context.business_operation = "revalidar";
            } else if (endpoint.includes("/reservar")) {
                context.business_operation = "reservar";
            } else if (method === "DELETE") {
                context.business_operation = "excluir";
            } else if (method === "POST") {
                context.business_operation = "criar";
            } else if (method === "PUT" || method === "PATCH") {
                context.business_operation = "editar";
            }
        } else if (endpoint.includes("/clientes")) {
            context.business_module = "clientes";
            this.sentryService.setTag("business_module", "clientes");
        } else if (endpoint.includes("/usuarios")) {
            context.business_module = "usuarios";
            this.sentryService.setTag("business_module", "usuarios");
        } else if (endpoint.includes("/auth")) {
            context.business_module = "autenticacao";
            this.sentryService.setTag("business_module", "autenticacao");
        }

        // Extrair ID da entidade se presente na URL
        const idMatch = endpoint.match(/\/(\d+)(?:\/|$)/);
        if (idMatch) {
            context.entity_id = idMatch[1];
            this.sentryService.setTag("entity_id", idMatch[1]);
        }
    }

    /**
     * Cria mensagem de erro descritiva
     */
    private createErrorMessage(
        error: HttpErrorResponse,
        method: string,
        endpoint: string
    ): string {
        const baseMessage = `API Error: ${method} ${endpoint}`;

        if (error.status === 0) {
            return `${baseMessage} - Network Error (conexão falhou)`;
        }

        if (error.status >= 500) {
            return `${baseMessage} - Server Error ${error.status} (erro interno do servidor)`;
        }

        if (error.status >= 400) {
            return `${baseMessage} - Client Error ${error.status} (${error.statusText})`;
        }

        return `${baseMessage} - HTTP ${error.status} (${error.statusText})`;
    }

    /**
     * Sanitiza headers removendo dados sensíveis
     */
    private sanitizeHeaders(headers: any): any {
        if (!headers) return null;

        const sanitized: any = {};
        const sensitiveHeaders = [
            "authorization",
            "cookie",
            "x-api-key",
            "x-auth-token",
        ];

        if (headers.keys) {
            // HttpHeaders object
            headers.keys().forEach((key: string) => {
                const lowerKey = key.toLowerCase();
                if (sensitiveHeaders.includes(lowerKey)) {
                    sanitized[key] = "[REDACTED]";
                } else {
                    sanitized[key] = headers.get(key);
                }
            });
        } else {
            // Plain object
            Object.keys(headers).forEach((key) => {
                const lowerKey = key.toLowerCase();
                if (sensitiveHeaders.includes(lowerKey)) {
                    sanitized[key] = "[REDACTED]";
                } else {
                    sanitized[key] = headers[key];
                }
            });
        }

        return sanitized;
    }

    /**
     * Sanitiza corpo da requisição removendo dados sensíveis
     */
    private sanitizeRequestBody(body: any): any {
        if (!body) return null;

        // Se for FormData, não podemos sanitizar facilmente
        if (body instanceof FormData) {
            return "[FormData]";
        }

        // Se for string, tentar fazer parse como JSON
        if (typeof body === "string") {
            try {
                body = JSON.parse(body);
            } catch {
                return "[String data]";
            }
        }

        return this.sanitizeObject(body);
    }

    /**
     * Sanitiza corpo da resposta removendo dados sensíveis
     */
    private sanitizeResponseBody(body: any): any {
        if (!body) return null;
        return this.sanitizeObject(body);
    }

    /**
     * Sanitiza objeto removendo campos sensíveis
     */
    private sanitizeObject(obj: any): any {
        if (!obj || typeof obj !== "object") return obj;

        const sensitiveFields = [
            "senha",
            "password",
            "token",
            "secret",
            "key",
            "authorization",
            "cpf",
            "cnpj",
            "rg",
            "passport",
            "ssn",
            "credit_card",
            "card_number",
        ];

        const sanitized = Array.isArray(obj) ? [] : {};

        for (const [key, value] of Object.entries(obj)) {
            const lowerKey = key.toLowerCase();

            if (sensitiveFields.some((field) => lowerKey.includes(field))) {
                (sanitized as any)[key] = "[REDACTED]";
            } else if (value && typeof value === "object") {
                (sanitized as any)[key] = this.sanitizeObject(value);
            } else {
                (sanitized as any)[key] = value;
            }
        }

        return sanitized;
    }
}
